<template>
  <el-form
    ref="instrumentForm"
    v-loading="isLoading"
    element-loading-text="載入測站與儀器資料中..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(255, 255, 255, 0.85)"
    :model="form"
    :validate-on-rule-change="false"
    :hide-required-asterisk="true"
    label-position="top"
    class="station-instrument-form"
  >
    <!-- 測站與專案篩選 -->
    <el-row :gutter="16">
      <el-col :span="12">
        <el-form-item
          prop="station"
          label="測站"
        >
          <el-select
            v-model="form.station"
            filterable
            style="width: 100%;"
          >
            <el-option
              v-for="station in stationList"
              :key="station._id"
              :value="station._id"
              :label="station.name"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item
          prop="project"
          label="專案篩選（多選追加儀器）"
        >
          <el-select
            v-model="selectedProjectIds"
            multiple
            collapse-tags
            filterable
            clearable
            placeholder="選擇專案以載入儀器"
            style="width: 100%;"
            @change="handleProjectChange"
          >
            <el-option
              v-for="project in projectList"
              :key="project._id"
              :value="project._id"
              :label="project.project_id || project.name || project._id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- 儀器過濾工具列 -->
    <div class="search-toolbar flex-row align-center justify-between mb-16">
      <el-input
        v-model="searchInput"
        placeholder="過濾清單（輸入關鍵字搜尋）..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        class="search-input"
        @input="instrumentFilter"
        @clear="instrumentFilter"
      >
        <template slot="append" v-if="searchUseReg">
          <el-tag size="mini" type="success" effect="dark">RegExp</el-tag>
        </template>
      </el-input>
      <div class="regex-toggle-wrapper flex-row align-center">
        <span class="regex-label">正則表達式</span>
        <el-tooltip content="支援正則表達式篩選，例如 TK\d+\+\d+" placement="top">
          <el-switch
            v-model="searchUseReg"
            active-color="#13ce66"
            inactive-color="#dcdfe6"
            @change="instrumentFilter"
          ></el-switch>
        </el-tooltip>
      </div>
    </div>

    <!-- 儀器配置與拖拉排序區域 -->
    <el-form-item
      prop="instrument"
      class="instrument-form-item"
    >
      <div slot="label" class="flex-row justify-between align-center label-header">
        <span class="form-item-title">
          <strong>儀器配置</strong>
          <span class="hint-text">（可直接拖曳 Tag 調整順序，點擊 ✕ 移除）</span>
        </span>
        <el-tag size="small" type="info">
          已選 {{ form.instrument.length }} / 未選 {{ candidateCount }}
        </el-tag>
      </div>
      <el-drag-select
        v-model="form.instrument"
        multiple
        filterable
        default-first-option
        placeholder="點擊下拉選單選取儀器，或輸入名稱 Enter 新增"
        class="full-width-drag-select"
      >
        <el-option
          v-for="instrument in instrumentList"
          :key="instrument._id"
          :label="instrument.name"
          :value="instrument._id"
        >
          <span>{{ instrument.name }}</span>
          <span
            v-if="getProjectLabel(instrument.project)"
            class="project-badge"
          >
            {{ getProjectLabel(instrument.project) }}
          </span>
        </el-option>
      </el-drag-select>
    </el-form-item>

    <!-- 底部操作列與提示 -->
    <div class="form-footer flex-row justify-between align-center">
      <div class="footer-tip">
        <i class="el-icon-info"></i>
        <span>調整順序請點擊「更新排序」；新增或刪減儀器請點擊「更新測站」</span>
      </div>
      <div class="button-group flex-row align-center">
        <el-button
          type="warning"
          icon="el-icon-sort"
          size="medium"
          :loading="isLoading"
          :disabled="!form.instrument.length || isLoading"
          @click="updateSelectSort"
        >
          更新排序
        </el-button>
        <el-button
          type="primary"
          icon="el-icon-check"
          size="medium"
          :loading="isLoading"
          :disabled="!selectedStation || isLoading"
          @click="submitForm"
        >
          更新測站
        </el-button>
      </div>
    </div>
  </el-form>
</template>

<script>
export default {
  props: {
    stationId: {
      type: String,
      default: '',
      required: false,
    },
    defaultProjectId: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isInitialized: false,
      selectedProjectIds: [],
      form: {
        station: '',
        instrument: [],
      },
      stationList: [],
      instrumentList: [],
      allInstrumentList: [],
      searchInput: '',
      searchUseReg: false,
    };
  },
  computed: {
    projectList() {
      return this.$store.getters['project/projectList'] || [];
    },
    projectMap() {
      const map = new Map();
      (this.projectList || []).forEach(p => {
        map.set(p._id, p.project_id || p.name || p._id);
      });
      return map;
    },
    selectedStation() {
      const selectedStationId = this.form.station;
      const selectedStation = this.stationList.find(ele => ele._id === selectedStationId);
      return selectedStation;
    },
    candidateCount() {
      const selectedProjSet = new Set(this.selectedProjectIds || []);
      const currentSelectedIds = new Set(
        (this.form.instrument || []).map(id => (typeof id === 'object' && id !== null ? id._id : id)),
      );
      return this.allInstrumentList.filter(inst => {
        if (currentSelectedIds.has(inst._id)) return true;
        const instProj = typeof inst.project === 'object' && inst.project !== null
          ? String(inst.project._id || inst.project)
          : String(inst.project || '');
        return selectedProjSet.has(instProj);
      }).length;
    },
    instrumentIdList() {
      return this.allInstrumentList.map(instrument => instrument._id);
    },
  },
  watch: {
    stationId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.form.station = newValue;
      }
    },
    'form.station'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.form.instrument = [];
        this.setStationInstrument();
        if (this.isInitialized && newValue) {
          this.resetProjectsForStation(newValue);
          this.searchInput = '';
          this.instrumentFilter();
        }
      }
    },
  },
  async mounted() {
    if (this.stationId) {
      this.form.station = this.stationId;
    }
    this.isLoading = true;
    try {
      await Promise.all([
        this.initProjectList(),
        this.getStationList(),
        this.getInstrumentList(),
      ]);
      this.setStationInstrument();
      this.resetProjectsForStation(this.form.station);
      this.instrumentFilter();
    } catch (e) {
      this.$notifyError(e);
    } finally {
      this.isLoading = false;
      this.isInitialized = true;
    }
  },
  methods: {
    async initProjectList() {
      const promises = [];
      if (!this.projectList || !this.projectList.length) {
        promises.push(this.$store.dispatch('project/getProjectList').catch(() => null));
      }
      const areaList = this.$store.getters['data/areaList'];
      if (!areaList || !areaList.length) {
        promises.push(this.$store.dispatch('data/getAreaList').catch(() => null));
      }
      if (promises.length) {
        await Promise.all(promises);
      }
    },
    getGlobalProjectId() {
      if (this.defaultProjectId) {
        return this.defaultProjectId;
      }
      const globalProj = this.$store.getters['project/selectedProject'];
      if (globalProj && globalProj._id) {
        return globalProj._id;
      }
      if (this.projectList && this.projectList.length > 0) {
        return this.projectList[0]._id;
      }
      return '';
    },
    getStationHostProjectId(stationId) {
      if (!stationId) return '';
      const areaList = this.$store.getters['data/areaList'] || [];
      const projectList = this.$store.getters['project/projectList'] || [];
      const area = areaList.find(a => Array.isArray(a.station) && a.station.includes(stationId));
      if (area) {
        const proj = projectList.find(p => Array.isArray(p.area) && p.area.includes(area._id));
        if (proj) return proj._id;
      }
      return '';
    },
    resetProjectsForStation(stationId) {
      const station = this.stationList.find(s => s._id === stationId);
      const boundInstruments = (station && Array.isArray(station.instrument)) ? station.instrument : [];

      if (boundInstruments.length > 0) {
        const matchedProjectIds = new Set();
        boundInstruments.forEach(instIdRaw => {
          const instId = typeof instIdRaw === 'object' && instIdRaw !== null ? String(instIdRaw._id) : String(instIdRaw);
          const found = this.allInstrumentList.find(i => i._id === instId);
          if (found && found.project) {
            const projId = typeof found.project === 'object' && found.project !== null
              ? String(found.project._id || found.project)
              : String(found.project);
            matchedProjectIds.add(projId);
          }
        });

        if (matchedProjectIds.size > 0) {
          this.selectedProjectIds = Array.from(matchedProjectIds);
          return;
        }

        const stationHostProj = this.getStationHostProjectId(stationId);
        if (stationHostProj) {
          this.selectedProjectIds = [stationHostProj];
          return;
        }
      }

      // If station has no instruments bound: reset to global navbar project ($store.getters['project/selectedProject'])
      const globalProjId = this.getGlobalProjectId();
      this.selectedProjectIds = globalProjId ? [globalProjId] : [];
    },
    getProjectLabel(projectId) {
      if (!projectId) return '';
      const pid = typeof projectId === 'object' && projectId !== null ? (projectId._id || projectId) : projectId;
      return this.projectMap.get(String(pid)) || '';
    },
    handleProjectChange() {
      this.instrumentFilter();
    },
    setStationInstrument() {
      if (this.selectedStation && Array.isArray(this.selectedStation.instrument)) {
        this.form.instrument = [...this.selectedStation.instrument];
      } else {
        this.form.instrument = [];
      }
    },
    getStationList(isUpdate = false) {
      return this.$store
        .dispatch('data/getStationList', isUpdate)
        .then(res => {
          this.stationList = res || [];
          return this.stationList;
        })
        .catch(this.$notifyError);
    },
    getInstrumentList(isUpdate = false) {
      return this.$store
        .dispatch('data/getInstrumentList', isUpdate)
        .then(res => {
          this.allInstrumentList = res || [];
          return this.allInstrumentList;
        })
        .catch(this.$notifyError);
    },
    instrumentFilter() {
      const selectedProjSet = new Set(this.selectedProjectIds || []);
      const currentSelectedIds = new Set(
        (this.form.instrument || []).map(id => (typeof id === 'object' && id !== null ? id._id : id)),
      );

      // 1. Filter by project & keep already-selected instruments (保底聯集)
      let pool = this.allInstrumentList.filter(inst => {
        if (currentSelectedIds.has(inst._id)) return true;
        const instProj = typeof inst.project === 'object' && inst.project !== null
          ? String(inst.project._id || inst.project)
          : String(inst.project || '');
        return selectedProjSet.has(instProj);
      });

      // 2. Filter by search input
      const query = this.searchInput ? this.searchInput.trim() : '';
      if (query) {
        if (this.searchUseReg) {
          try {
            const reg = new RegExp(query, 'i');
            pool = pool.filter(ele => reg.test(ele.name) || currentSelectedIds.has(ele._id));
          } catch (e) {
            // regex error fallback
          }
        } else {
          const lowerQuery = query.toLowerCase();
          pool = pool.filter(ele => (ele.name && ele.name.toLowerCase().includes(lowerQuery)) || currentSelectedIds.has(ele._id));
        }
      }

      // 3. Ensure all currently selected instruments are present in instrumentList so Element UI renders labels in tags
      const map = new Map();
      pool.forEach(item => map.set(item._id, item));
      currentSelectedIds.forEach(id => {
        if (!map.has(id)) {
          const found = this.allInstrumentList.find(i => i._id === id);
          if (found) {
            map.set(id, found);
          } else {
            map.set(id, { _id: id, name: id, project: '' });
          }
        }
      });

      this.instrumentList = Array.from(map.values());
    },
    async updateSelectSort() {
      let message = '此操作將改變儀器排序';
      const response = await this.$confirmCreate(message);
      if (response === 'confirm') {
        this.isLoading = true;
        let instrument = this.form.instrument;
        if (!instrument.length) {
          this.$notifyError('儀器列表不能為空');
          this.isLoading = false;
          return;
        }

        try {
          await this.removeAllInstrumentFromStation();
          await this.addInstrumentToStation(instrument);

          this.$notifySuccess('修改成功');
          await this.getStationList(true);
          await this.getInstrumentList(true);
          this.setStationInstrument();
          this.instrumentFilter();
        } catch (error) {
          console.log(error);
          this.$notifyError(error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async removeAllInstrumentFromStation() {
      return this.$axios({
        category: 'admin',
        method: 'delete',
        apiName: `station/${this.form.station}/allinstrument`,
        data: {
          area: [],
        },
      }).catch(this.$notifyError);
    },
    submitForm() {
      this.$refs.instrumentForm.validate(async valid => {
        if (valid) {
          this.updateStationInstrument();
        } else {
          return false;
        }
      });
    },
    async addInstrumentToStation(instrumentIdList) {
      if (!instrumentIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'post',
        apiName: `station/${this.form.station}/instrument`,
        data: {
          instrument: instrumentIdList,
        },
      }).catch(this.$notifyError);
    },
    async removeInstrumentFromStation(instrumentIdList) {
      if (!instrumentIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'delete',
        apiName: `station/${this.form.station}/instrument`,
        data: {
          instrument: instrumentIdList,
        },
      }).catch(this.$notifyError);
    },
    async createStationNewInstrument(instrumentIdList) {
      if (!instrumentIdList.length) return;
      return this.$axios({
        category: 'admin',
        method: 'post',
        apiName: 'station',
        data: {
          station: this.form.station,
          instrument: instrumentIdList,
        },
      }).catch(this.$notifyError);
    },
    async updateStationInstrument() {
      const currentStationInstruments = (this.selectedStation && this.selectedStation.instrument) || [];
      const knownIds = new Set(this.allInstrumentList.map(i => i._id));
      const isExisting = id => knownIds.has(id);

      const removedInstrument = currentStationInstruments.filter(instrument => {
        return !this.form.instrument.includes(instrument);
      });

      const newAddedInstrument = this.form.instrument.filter(instrument => {
        return !isExisting(instrument) && !currentStationInstruments.includes(instrument);
      });

      const pushedInstrument = this.form.instrument.filter(instrument => {
        return !currentStationInstruments.includes(instrument) && isExisting(instrument);
      });

      if (!newAddedInstrument.length && !removedInstrument.length && !pushedInstrument.length) {
        return this.$notifyInfo('沒有改動');
      }

      let message = '此操作將';
      if (newAddedInstrument.length) {
        message += `新增 ${newAddedInstrument.length} 個儀器並加至該測站\n`;
      }
      if (removedInstrument.length) {
        message += `從該測站移除 ${removedInstrument.length} 個儀器\n`;
      }
      if (pushedInstrument.length) {
        message += `更新 ${pushedInstrument.length} 個儀器至該測站\n`;
      }
      const response = await this.$confirmCreate(message);
      if (response === 'confirm') {
        this.isLoading = true;
        Promise.all([
          this.createStationNewInstrument(newAddedInstrument),
          this.removeInstrumentFromStation(removedInstrument),
          this.addInstrumentToStation(pushedInstrument),
        ])
          .then(async () => {
            this.$notifySuccess('新增成功');
            await this.getStationList(true);
            await this.getInstrumentList(true);
            this.setStationInstrument();
            this.instrumentFilter();
          })
          .catch(error => {
            this.$notifyError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.station-instrument-form {
  min-width: 620px;
  max-width: 760px;
  min-height: 280px;

  ::v-deep .el-form-item {
    margin-bottom: 18px;
  }

  ::v-deep .el-select,
  ::v-deep .full-width-drag-select {
    width: 100%;
  }

  ::v-deep .drag-select {
    display: block;
    position: relative;
    width: 100%;

    .el-select__tags {
      position: relative !important;
      top: auto !important;
      left: auto !important;
      transform: none !important;
      width: 100% !important;
      max-width: 100% !important;
      min-height: 42px !important;
      padding: 5px 36px 5px 10px !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      z-index: 1;

      > span {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      }
    }

    .el-tag {
      margin: 3px 4px 3px 0;
      cursor: grab;
      user-select: none;
      &:active {
        cursor: grabbing;
      }
    }

    .el-select__input {
      margin-left: 6px;
      height: 28px;
      line-height: 28px;
    }

    .el-input {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      pointer-events: none;
      z-index: 0;

      .el-input__inner {
        height: 100% !important;
        min-height: 42px !important;
      }

      .el-input__suffix {
        pointer-events: auto;
        display: flex;
        align-items: center;
        right: 8px;
      }
    }
  }
}

.search-toolbar {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #ebeef5;

  .search-input {
    max-width: 380px;
  }

  .regex-toggle-wrapper {
    display: flex;
    align-items: center;
  }

  .regex-label {
    font-size: 13px;
    color: #606266;
    margin-right: 8px;
  }
}

.label-header {
  width: 100%;
  margin-bottom: 4px;
}

.form-item-title {
  font-size: 14px;
  color: #303133;
}

.hint-text {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 6px;
}

.project-badge {
  float: right;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  margin-left: 12px;
}

.form-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.footer-tip {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  i {
    margin-right: 5px;
    color: #e6a23c;
    font-size: 14px;
  }
}

.button-group {
  display: flex;
  align-items: center;
  .el-button + .el-button {
    margin-left: 12px;
  }
}
</style>
